import { Component, inject, OnInit, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Table, TableModule } from 'primeng/table';
import { OperateurExport } from '../../../../data/edition/models/operateur-export.model';
import { OperateursService } from '../../../../data/intervenant/services/operateurs/operateurs.service';
import { SessionContext } from '../../../../core/services/config/app.settings';
import { LoaderService } from '../../../../core/services/loader/loader.service';
import { Button } from 'primeng/button';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';
import { ModalService } from '../../../../shared/services/modal/modal.service';
import { Column } from '../../../../shared/models/column.model';
import { ColumnVisibilityModalComponent } from '../../../../shared/components/modals/column-visibility-modal/column-visibility-modal.component';
import moment from 'moment/moment';
import { ExportToCsv } from 'export-to-csv';
import { ExportService } from '../../../../data/export/services/export.services';

@Component({
    selector: 'app-generation-operateurs',
    templateUrl: './generation-operateurs.component.html',
    styleUrls: ['./generation-operateurs.component.scss'],
    imports: [CommonModule, Button, FaIconComponent, TranslateModule, TableModule, ColumnVisibilityModalComponent],
    standalone: true
})
export class GenerationOperateursComponent implements OnInit {
    /**
     * Signals
     */
    showColumnVisibilityModal = signal(false);

    /**
     * Properties
     */
    @ViewChild('dt') dt: Table;
    operateurs: OperateurExport[] = [];
    isResult: boolean = false;
    columns: Column[] = [
        { field: 'nom', label: 'Nom', visible: true, filter: 'text', width: '15%' },
        { field: 'prenom', label: 'Prénom', visible: true, filter: 'text', width: '15%' },
        { field: 'raisonSociale', label: 'Raison Sociale', visible: true, filter: 'text', width: '40%' },
        { field: 'siret', label: 'SIRET', visible: true, filter: 'text', width: '15%' },
        { field: 'cvi', label: 'CVI', visible: true, filter: 'text', width: '15%' },
        { field: 'structureJuridique', label: 'Structure juridique', visible: false, filter: 'text', width: '15%' },
        { field: 'adresse', label: 'Adresse', visible: false, filter: 'text', width: '15%' },
        { field: 'codePostal', label: 'Code Postal', visible: false, filter: 'text', width: '15%' },
        { field: 'ville', label: 'Ville', visible: false, filter: 'text', width: '15%' },
        { field: 'telephoneMobile', label: 'Téléphone', visible: false, filter: 'text', width: '15%' },
        { field: 'email', label: 'Courriel', visible: false, filter: 'text', width: '15%' }
    ];
    readonly STORAGE_KEY = 'operateur_columns_visibility';

    /**
     * Getters
     */
    get idOrganisme(): number {
        return SessionContext.get('idOrganisme');
    }

    /**
     * Dependency injections
     */
    private readonly operateursService = inject(OperateursService);
    private readonly loaderService = inject(LoaderService);
    protected readonly modalService = inject(ModalService);
    private readonly exportService = inject(ExportService);

    /**
     * On init component
     */
    ngOnInit(): void {
        this.isResult = false;
        this.operateursService
            .getOperateursByOrganisme(this.idOrganisme)
            .pipe(this.loaderService.operator())
            .subscribe({
                next: operateurs => {
                    this.columns = this.modalService.loadColumnVisibility(this.STORAGE_KEY, this.columns);
                    this.operateurs = operateurs.sort((a, b) => a.raisonSociale.localeCompare(b.raisonSociale));
                    this.isResult = true;
                }
            });
    }

    /**
     * Open column visibility modal
     */
    openColumnVisibilityModal(): void {
        this.showColumnVisibilityModal.set(true);
    }

    /**
     * Export operators to CSV
     */
    exporterOperateurs(): void {
        const data = [];
        const operateursAExporter = this.dt.filteredValue ?? this.operateurs;

        operateursAExporter.forEach(operateur => {
            data.push({
                nom: this.exportService.clean(operateur?.nom),
                prenom: this.exportService.clean(operateur?.prenom),
                raisonSociale: this.exportService.clean(operateur?.raisonSociale),
                siret: operateur?.siret,
                cvi: operateur?.cvi,
                structureJuridique: this.exportService.clean(operateur?.structureJuridique),
                adresse: this.exportService.clean(operateur?.adresse),
                codePostal: operateur?.codePostal,
                ville: this.exportService.clean(operateur?.ville),
                telephone: operateur?.telephoneMobile
            });
        });

        const options = {
            filename: 'EDI_INAO_OP'.concat('_').concat(moment().format('YYYYMMDD')),
            fieldSeparator: ';',
            quoteStrings: '',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: false,
            title: 'EDI_INAO_OP'.concat('_').concat(moment().format('YYYYMMDD')),
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: false,
            headers: ['Nom', 'Prénom', 'Raison sociale', 'SIRET', 'CVI', 'Structure juridique', 'Adresse', 'Code postal', 'Ville', 'Téléphone']
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(data);
    }
}
